import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import {
  IconChartLine,
  IconChartPie,
  IconClipboardCheck,
  IconCloudComputing,
  IconDownload,
  IconHierarchy2,
  IconTargetArrow,
} from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { Container, Heading, Text } from '../atomic';

/**
 * @typedef {Object} FeatureCard
 * @property {string} id - Feature identifier
 * @property {string} title - Feature title
 * @property {string} description - Feature description
 * @property {React.ReactNode} icon - Feature icon
 * @property {string} size - Card size ('large' | 'medium' | 'wide' | 'small')
 * @property {number} [gridColumn] - Grid column span
 * @property {number} [gridRow] - Grid row span
 */

/**
 * @typedef {Object} ProductFeaturesBentoGridProps
 * @property {string} [sectionId] - Section ID for navigation
 * @property {string} [headline] - Section headline
 * @property {string} [subheadline] - Section subheadline
 */

/**
 * Product Features Bento Grid Section
 * Asymmetric bento layout with product-focused feature cards
 *
 * @param {ProductFeaturesBentoGridProps} props - Component props
 * @returns {JSX.Element} Features bento grid section
 */
function ProductFeaturesBentoGrid({
  sectionId = 'features',
  headline = 'Core Capabilities',
  subheadline = 'Everything you need to transform cloud costs into financial insights',
}) {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  /** @type {FeatureCard[]} */
  const features = [
    {
      id: 'allocation',
      title: 'Cost Allocation Engine',
      description:
        'Map AWS costs to your chart of accounts automatically. Define allocation rules that distribute costs across departments, projects, and cost centers based on actual usage.',
      icon: (
        <IconChartPie
          size={40}
          stroke={1.5}
          color={theme.palette.primary.main}
        />
      ),
      size: 'large',
      gridColumn: isMobile ? 1 : 2,
      gridRow: isMobile ? 1 : 2,
    },
    {
      id: 'hierarchies',
      title: 'Financial Hierarchies',
      description:
        'Define cost centers, departments, and projects your way. Build multi-level hierarchies that match your organizational structure.',
      icon: (
        <IconHierarchy2
          size={32}
          stroke={1.5}
          color={theme.palette.primary.main}
        />
      ),
      size: 'medium',
      gridColumn: 1,
    },
    {
      id: 'sync',
      title: 'Real-time Sync',
      description:
        'Always up-to-date with AWS billing data. Automatic sync ensures your cost data reflects the latest changes.',
      icon: (
        <IconCloudComputing
          size={32}
          stroke={1.5}
          color={theme.palette.primary.main}
        />
      ),
      size: 'medium',
      gridColumn: 1,
    },
    {
      id: 'export',
      title: 'Export Anywhere',
      description:
        'QuickBooks, NetSuite, Excel, or any ERP. Export your data in formats that integrate seamlessly with your existing workflows.',
      icon: (
        <IconDownload
          size={32}
          stroke={1.5}
          color={theme.palette.primary.main}
        />
      ),
      size: 'wide',
      gridColumn: isMobile ? 1 : 3,
    },
    {
      id: 'variance',
      title: 'Variance Analysis',
      description: 'Understand cost drivers and budget deviations',
      icon: (
        <IconChartLine
          size={28}
          stroke={1.5}
          color={theme.palette.primary.main}
        />
      ),
      size: 'small',
      gridColumn: 1,
    },
    {
      id: 'budget',
      title: 'Budget Tracking',
      description: 'Real-time budget vs. actual monitoring',
      icon: (
        <IconTargetArrow
          size={28}
          stroke={1.5}
          color={theme.palette.primary.main}
        />
      ),
      size: 'small',
      gridColumn: 1,
    },
    {
      id: 'audit',
      title: 'Audit Trail',
      description: 'Complete history for compliance and audits',
      icon: (
        <IconClipboardCheck
          size={28}
          stroke={1.5}
          color={theme.palette.primary.main}
        />
      ),
      size: 'small',
      gridColumn: 1,
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  /**
   * Render a feature card
   * @param {FeatureCard} feature - Feature data
   * @param {number} index - Feature index
   * @returns {JSX.Element} Feature card component
   */
  const renderFeatureCard = (feature, index) => {
    const isLarge = feature.size === 'large';
    const isWide = feature.size === 'wide';
    const isSmall = feature.size === 'small';

    return (
      <Box
        key={feature.id}
        sx={{
          gridColumn: `span ${feature.gridColumn || 1}`,
          gridRow: feature.gridRow ? `span ${feature.gridRow}` : 'auto',
          backgroundColor: '#ffffff',
          borderRadius: isLarge || isWide ? '24px' : '16px',
          p: isLarge ? { xs: 3, md: 4 } : isSmall ? 2.5 : 3,
          display: 'flex',
          flexDirection: 'column',
          gap: isLarge ? 2 : 1.5,
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
          border: '1px solid rgba(0, 0, 0, 0.06)',
          transition: 'all 0.3s ease',
          cursor: 'default',
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
          transitionDelay: `${index * 80}ms`,
          '&:hover': {
            transform: isVisible ? 'translateY(-6px) scale(1.02)' : 'none',
            boxShadow: '0 12px 40px rgba(94, 77, 137, 0.12)',
          },
        }}
      >
        <Box>{feature.icon}</Box>
        <Typography
          variant={isLarge ? 'h4' : isWide ? 'h5' : 'h6'}
          sx={{
            fontWeight: 600,
            color: theme.palette.primary.main,
            fontSize: isLarge
              ? { xs: '1.25rem', md: '1.5rem' }
              : isSmall
                ? '1rem'
                : '1.1rem',
          }}
        >
          {feature.title}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: '#6c757d',
            lineHeight: 1.7,
            fontSize: isLarge ? '1rem' : isSmall ? '0.875rem' : '0.95rem',
            flex: 1,
          }}
        >
          {feature.description}
        </Typography>
      </Box>
    );
  };

  return (
    <Box
      component="section"
      id={sectionId}
      ref={sectionRef}
      aria-label="Product features"
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: '#f8f9fa',
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Heading
            variant="h2"
            sx={{
              fontSize: { xs: '1.75rem', md: '2.5rem' },
              fontWeight: 700,
              color: theme.palette.primary.main,
              mb: 2,
            }}
          >
            {headline}
          </Heading>
          <Text
            sx={{
              fontSize: { xs: '1rem', md: '1.2rem' },
              color: '#6c757d',
              maxWidth: '600px',
              mx: 'auto',
              lineHeight: 1.7,
            }}
          >
            {subheadline}
          </Text>
        </Box>

        {/* Bento Grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
            },
            gap: { xs: 2, md: 3 },
          }}
        >
          {features.map((feature, index) => renderFeatureCard(feature, index))}
        </Box>
      </Container>
    </Box>
  );
}

export default ProductFeaturesBentoGrid;
